import React, { type ReactNode } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { ChipMultiSelect } from '../../forms/ChipMultiSelect/ChipMultiSelect';

import type {
  CatalogFilterState,
  CatalogLabels,
  ScopeFilter,
} from './ResourceCatalog.types';

export interface CatalogFilterBarProps {
  filters: CatalogFilterState;
  onChange: (filters: CatalogFilterState) => void;
  /** Distinct owner labels present in the data (see collectOwnerOptions). */
  ownerOptions: string[];
  labels: CatalogLabels;
  /** Resource-specific action rendered at the far right (composition seam). */
  action?: ReactNode;
}

/**
 * Controlled filter row: scope toggle, owner multi-select, name search, and
 * the show-archived switch. Scope uses a ToggleButtonGroup (three fixed
 * options — cheaper to scan than a dropdown); owners reuse ChipMultiSelect.
 */
export const CatalogFilterBar: React.FC<CatalogFilterBarProps> = ({
  filters,
  onChange,
  ownerOptions,
  labels,
  action,
}) => {
  const handleScopeChange = (
    _event: React.MouseEvent<HTMLElement>,
    scope: ScopeFilter | null
  ): void => {
    // ToggleButtonGroup emits null when the active button is re-clicked.
    if (scope !== null) onChange({ ...filters, scope });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filters.scope}
        onChange={handleScopeChange}
        aria-label="Scope filter"
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="shared">{labels.sharedScopeLabel}</ToggleButton>
        <ToggleButton value="personal">{labels.personalScopeLabel}</ToggleButton>
      </ToggleButtonGroup>

      <ChipMultiSelect
        labelId="resource-catalog-owner-filter"
        label={labels.ownerFilterLabel}
        value={filters.owners}
        options={ownerOptions.map((owner) => ({ value: owner, label: owner }))}
        onChange={(owners): void => onChange({ ...filters, owners })}
        onOnly={(owner): void => onChange({ ...filters, owners: [owner] })}
        onClear={(): void => onChange({ ...filters, owners: [] })}
        minWidth={180}
      />

      <TextField
        size="small"
        value={filters.search}
        onChange={(event): void =>
          onChange({ ...filters, search: event.target.value })
        }
        placeholder={`Search ${labels.nounPlural}`}
        slotProps={{
          htmlInput: { 'aria-label': `Search ${labels.nounPlural}` },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment:
              filters.search !== '' ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={(): void => onChange({ ...filters, search: '' })}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          },
        }}
        sx={{ minWidth: 300 }}
      />

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={filters.showArchived}
            onChange={(event): void =>
              onChange({ ...filters, showArchived: event.target.checked })
            }
          />
        }
        label="Show archived"
        sx={{ ml: 'auto' }}
      />

      {action}
    </Box>
  );
};

CatalogFilterBar.displayName = 'CatalogFilterBar';
